import { ListContextProvider, useGetMainList, useListContext, useListController, useRecordContext } from "ra-core"
import { Content } from "~/components/content/Content"
import { OdResourcePageProps } from "~/types/resource"
import { Box, Card, CardHeader, Divider, Autocomplete, TextField as MuiTextField, Backdrop, Typography } from "@mui/material"
import { useFormik, useFormikContext } from "formik";
import { FC, useEffect, useMemo, useRef } from "react";
import { Datagrid } from "~/components/list/datagrid"
import Scrollbar from "~/components/Scrollbar"
import { RecPatch } from "~/dataProvider/tools"
import { FunctionField } from "~/components/field"
import { EmailType, NotificationAssignDto, RoleResponseDto } from "~/gen/api/user"
import { pages, sorts } from "~/utils/paramsWithGetMainList"
import OdForm from "~/components/formik/OdForm";
import { cloneDeep, debounce } from "lodash"
import { useResourceRegister } from "~/utils/resourceRegister"
import CreateOrEdit from "~/controller/CreateOrEdit"
import { useSimpleSaveAction } from "~/controller/SaveContextProvider"
import { useDefaultSavedHandlers } from "~/controller/defaultSavedHandlers"
import { useHasPermit } from "~/authProvider/useHasPermit";
import { ORGANIZATION_USERS } from "~/constants";

  
const NotificationSettings: FC<OdResourcePageProps> = (props) => {
  useResourceRegister({ name: 'org-roles' })
  const { resource } = props

  const listCtrl = useListController({ resource, basePath: '' })
  return (
    <ListContextProvider value={listCtrl}>
      <NotificationSettingsBody {...props} />
    </ListContextProvider>
  )
}


const NotificationSettingsBody: FC<OdResourcePageProps> = ({
  options,
  resource,
}) => {
  const listCtx = useListContext<NotificationAssignDto & RecPatch>()
  const { data, refetch } = listCtx

  const { loaded, data: rolesData } = useGetMainList<RoleResponseDto&RecPatch>('org-roles', pages(), sorts(), {}, { action: 'CUSTOM_DATA_QUERY' })
  const roles = Object.values(rolesData || {}).sort((a, b) => a.id - b.id)

  const initialValues = useMemo(() => cloneDeep(data), [data])

  const formikCtrl = useFormik({ initialValues, onSubmit: () => {} })
  const { setValues } = formikCtrl

  useEffect(() => {
    if (initialValues) {
      setValues(initialValues)
    }
  }, [initialValues, setValues])

  const rolesAssignHandlers = useDefaultSavedHandlers("Update Roles Assignment")
  rolesAssignHandlers.onSuccess.merge(refetch)

  return (
    <Content pageName="Notification Settings" resourceName="Notification Settings" breadcrumbs={options?.breadcrumbs}>
      <Card sx={{ mt: 5 }}>
        <CardHeader title="Notification Email Targets" />
        <Divider />
        <Scrollbar>
          <Box sx={{ minWidth: 1150 }}>
            <OdForm formikCtrl={formikCtrl}>
              <Datagrid 
                hasBulkActions={false} 
                sx={{ 
                  '& th' : { py: 2, '& span': { display: 'inline-block' } },
                  '& td' : { py: 2 },
                }}
                {...listCtx}
              >
                <FunctionField
                  label={<Box component="span" sx={{ maxWidth: 230 }}>Nofitication Type</Box>}
                  sortable={false}
                  render={(d?:NotificationAssignDto&RecPatch) => (
                    d && d.notificationType &&
                    <Typography component="span" color="textPrimary" variant="body2">
                      {notiTypeInfoMap[d.notificationType]?.name}
                    </Typography>
                  )}
                />
                <FunctionField
                  source="roles"
                  label="Roles Assign"
                  sortable={false}
                  render={(d:any) => (
                    <CreateOrEdit 
                      id={d.id}
                      record={d} 
                      basePath="" 
                      resource={resource} 
                      mutationMode="pessimistic" 
                      {...rolesAssignHandlers}
                    >
                      <RolesAssign roles={roles} />
                    </CreateOrEdit>
                  )}
                />
                <FunctionField 
                  label="Description"
                  sortable={false}
                  render={(d?:NotificationAssignDto&RecPatch) => (
                    d && d.notificationType &&
                    <Typography color="textSecodary" variant="caption" sx={{ maxWidth: 250, display: 'inline-block' }}>
                      {notiTypeInfoMap[d.notificationType]?.description}
                    </Typography>
                  )}
                />
              </Datagrid>
            </OdForm>
          </Box>
        </Scrollbar>
      </Card>
      <Backdrop open={!loaded} />
    </Content>
  )
}

const notiTypeInfoMap = {
  [EmailType.GenericAlert]: { name: 'Generic Alert', description: 'General purpose notification that does not belong to a specific topic.' },
  [EmailType.BackupAlert]: { name: 'Backup Alert', description: 'Notification of Warning or Error status associated with database backup.' },
  [EmailType.BackupNotReceivedAlert]: { name: 'Backup Not Received Alert', description: 'Notification on missing database backup for more than one day.' },
  [EmailType.ValidationAlert]: { name: 'Validation Alert', description: 'Notification of Warning or Error status associated with the validation of database backups.' },
} as Record<EmailType, {name:string, description:string}>

export default NotificationSettings;

const RolesAssign: FC<{roles: RoleResponseDto[]}> = ({ roles }) => {
  const hasWritePermit = useHasPermit([ORGANIZATION_USERS])
  const ntaDto = useRecordContext<NotificationAssignDto&RecPatch>()
  const emailType = ntaDto.notificationType ?? EmailType.Unknown
  const { values, setFieldValue } = useFormikContext<Record<EmailType, NotificationAssignDto>>()
  const assignedDto = values[emailType]
  const rolesSelected = useMemo(() => {
    const s1 = new Set<number>(assignedDto?.roles ?? [])
    const s2 = new Set<number>(assignedDto?.customRoles ?? [])
    return (roles ?? []).flatMap(r => {
      const s = r.organizationId === 0 ? s1 : s2
      return s.has(r.roleId!) ? [r] : []
    })
  }, [assignedDto, roles.length])
  
  const save = useSimpleSaveAction()
  const saveRef = useRef(save)
  if (saveRef.current !== save) {
    saveRef.current = save
  }
  const saveDebounced = useMemo(() => debounce((a, b) => saveRef.current(a, b) , 1200), [])

  return (
    <Box sx={{ width: 500 }}>
      <Autocomplete
        disabled={!hasWritePermit}
        multiple
        options={roles}
        getOptionLabel={(option:RoleResponseDto) => option.name || ''}
        value={rolesSelected}
        filterSelectedOptions
        renderInput={(params) => (
          <MuiTextField
            {...params}
            placeholder="Choose roles"
            inputProps={{
              ...params.inputProps,
              'aria-label': ntaDto.notificationType !== undefined 
                ? notiTypeInfoMap[ntaDto.notificationType]?.name
                : '',
            }}
          />
        )}
  
        onChange={(e, value, reason) => {
          if (reason === 'selectOption' || reason === 'removeOption') {
            const assign = values[emailType]
            const roles = value.reduce((acc, r) => {
              const arr = r.organizationId === 0 ? acc.roles : acc.customRoles
              arr.push(r.roleId!)
              arr.sort()
              return acc
            }, { roles: [] as number[], customRoles: [] as number[] })
            setFieldValue(emailType+'', 
              {
                ...assign,
                ...roles,
              }
            )
            saveDebounced({ id: emailType, roles }, {
              onSuccess: () => {
                ntaDto.roles = roles.roles
                ntaDto.customRoles = roles.customRoles
              }
            })
          }
        }}
      />
    </Box>
  )
}

